import { Article } from "@/typings/article";
import noodleUrl from "@/images/noodle.webp";

export function formatDate(isoDate: string): string {
  const date = new Date(isoDate);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export const articles: Array<Article> = [
  {
    title: "How to make a pigeon think better",
    date: "2025-11-15",
    content: (
      <>
        <p>
          Over the past weeks, I've been making some improvements to the pigeon guessing logic. Each
          guess calls OpenAI with the current drawing, as well as the previous rounds' guesses and
          their scores. My prompt instructs the model to guess similarly to a previous guess if it
          scored similarly, but differently if it didn't.
        </p>
        <p>
          But there's some tweaking involved there! For example, I recently couldn't get the pigeon
          to guess "lemon" because it kept on guessing variations of "orange" and they kept on
          scoring well enough that the guesses continued ("orange slice", "oranges", etc). So I had
          to adjust the prompt to mix it up a bit if the guesses get stuck on a particular topic,
          plus to stop it from just guessing pluralizations of previous guesses. We'll see if it
          helps!
        </p>
      </>
    ),
  },
  {
    title: "So many pigeon puns",
    date: "2025-10-17",
    content: (
      <>
        <p>
          Why is the name of this site DoodlyCoo? Well, originally I was going to be more punny and
          call it <i>pigeonary</i> (get it? It's a portmanteau of <i>pigeon</i> and{" "}
          <i>pictionary</i>!) But pigeonary is harder to say, plus I think Mattel owns the rights to
          Pictionary and I don't want to risk getting sued by them.
        </p>
        <p>
          After throwing around way too many pigeon puns (Pidgecasso? Coo Drawings?), I looked for
          other sources of inspiration. One of them being my dog Noodle, affectionately nicknamed
          NoodlyDoo. I honestly don't remember if it was me or my husband who made the jump to
          DoodlyCoo, but it stuck!
        </p>
        <img src={noodleUrl} alt="My dog Noodle" />
        <p>Thanks, buddy!</p>
      </>
    ),
  },
  {
    title: "Oh come on pigeon, you know what this is",
    date: "2025-10-10",
    content: (
      <>
        <p>
          Now that I built this thing, I'm figuring out how to make it work better, specifically
          what types of phrases work the best. As an added layer of complexity, I've been trying to
          give each week a loose theme, and have the phrases increase in difficulty as the week
          progresses, with the easiest one on Monday. So currently writing up daily phrases is
          happening under the wire, like that{" "}
          <a
            href="https://media1.tenor.com/m/ov_A6LpzssAAAAAd/wallace-and-gromit-wallace.gif"
            target="_blank"
          >
            GIF of Gromit laying down railroad tracks just in front of the train
          </a>
          .
        </p>
        <p>
          One day, I'm hoping to pump out a bunch of phrases at once, but I'm still feeling it out
          and trying to better understand what makes a good DoodlyCoo phrase versus a bad one.
        </p>
        <p>
          "Syrup" for example, was a terrible phrase. The pigeon didn't know what to make of an
          amorphous goo, even if it was on top of pancakes, plus my drawing ability using a trackpad
          and the time limit didn't help. Incredibly specific phrases have been fun but still hard,
          like when I had to figure out how to distinctively draw a persimmon. (spoilers:
          "Persimmon" is coming up soon)
        </p>
        <p>The work continues!</p>
      </>
    ),
  },
  {
    title: "Are pigeons real?",
    date: "2025-10-09",
    content: (
      <>
        <p>
          The DoodlyCoo pigeon may not be real, but I'd like to think I put some effort into it.
        </p>
        <p>
          The pigeon guessing logic is currently powered by OpenAI. Specifically, it's used for
          image analysis to get the guess based on your drawing, and then for getting embeddings to
          score how close your drawing was to the original prompt based on that guess. The score is
          not only used to decide if you've succeeded or failed, but also determines whether to play
          a happy or sad animation on the pigeon.
        </p>
        <p>
          My main concerns here besides the actual guessing accuracy were not totally going over my
          budget with API requests and also not having the pigeon spend too much time thinking. This
          has mostly involved tuning the input image size to be smaller but still distinguishable,
          and tuning which model I'm using. I originally built out a guessing agent as well that
          would return a pigeon animation to display based on the score, but opted to save time and
          money by removing that and keeping the API calls simple.
        </p>
        <p>
          These pigeon animations started off as sketches using pencil and paper, and were then
          traced and finished in an image editor. Same with all the image assets, really. With all
          this effort put in, you'd think I'd name the pigeon…
        </p>
      </>
    ),
  },
  {
    title: "Welcome!",
    date: "2025-10-04",
    content: (
      <>
        <p>
          DoodlyCoo is now live, I hope you enjoy it! This started off as a random idea of making a
          silly game with AI…and I guess that's exactly what I created.
        </p>
        <p>
          You might be asking, "Why a pigeon?". I figured pigeons live closely with people so you'd
          expect them to be good at recognizing images of human concepts - the perfect drawing game
          partner! But since pigeons are pretty bird brained, even for birds, you also won't be too
          angry when they get things hilariously wrong (hopefully).
        </p>
        <p>
          I'll be continuing to refine the prompts and pigeon guessing logic, as well as generally
          fixing any issues, so stay tuned!
        </p>
      </>
    ),
  },
];
